import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { Package, AlertTriangle, ArrowLeft, RefreshCw, Box } from "lucide-react";
import { Link } from "react-router-dom";
import { showToast } from "../../utils/toastUtils";

const BASE_URL = process.env.REACT_APP_API_URL;

const LowStockDashboard = () => {
    const { token } = useContext(AuthContext);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    if (token) {
        axios.defaults.headers.common["x-auth-token"] = token;
    }

    const fetchLowStockProducts = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${BASE_URL}/api/products`);
            // Filter locally for now effectively
            const lowStock = res.data.filter(p => (p.stock || 0) < 10);
            setProducts(lowStock);
        } catch (err) {
            console.error("Error fetching products:", err);
            showToast("Error", "Failed to fetch low stock products", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLowStockProducts();
    }, [token]);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <Link to="/summary" className="inline-flex items-center text-gray-500 hover:text-gray-700 mb-2 transition-colors">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
                        </Link>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-amber-500" /> Low Stock Dashboard
                        </h1>
                        <p className="mt-1 text-gray-500">
                            Products with stock quantity less than 10.
                        </p>
                    </div>
                    <button
                        onClick={fetchLowStockProducts}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-all shadow-sm"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center text-gray-500">Loading...</div>
                    ) : products.length === 0 ? (
                        <div className="p-12 text-center flex flex-col items-center">
                            <Box className="w-12 h-12 text-green-200 mb-3" />
                            <h3 className="text-lg font-medium text-gray-900">Good news!</h3>
                            <p className="text-gray-500">No products are currently low in stock.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm text-gray-600">
                                <thead className="bg-gray-50 text-xs uppercase text-gray-500 font-semibold border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-4">Product Name</th>
                                        <th className="px-6 py-4">Category</th>
                                        <th className="px-6 py-4 text-center">Stock Level</th>
                                        <th className="px-6 py-4 text-right">Price</th>
                                        <th className="px-6 py-4 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {products.map((product) => (
                                        <tr key={product._id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4 font-medium text-gray-900 flex items-center gap-3">
                                                {product.image ? (
                                                    <img
                                                        src={`${BASE_URL}${product.image}`}
                                                        alt={product.productName}
                                                        className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                                                        <Package className="w-5 h-5 text-gray-400" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-semibold">{product.productName}</div>
                                                    <div className="text-xs text-gray-400">Item No: {product.itemNo}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">{product.productType || "Uncategorized"}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.stock === 0 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                                                    }`}>
                                                    {product.stock}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">Rs. {product.price?.toFixed(2)}</td>
                                            <td className="px-6 py-4 text-center">
                                                {product.stock === 0 ? (
                                                    <span className="text-xs font-semibold text-red-600">Out of Stock</span>
                                                ) : (
                                                    <span className="text-xs font-semibold text-amber-600">Low Stock</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LowStockDashboard;
